import React from "react";
import { Link } from "react-router-dom";
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Container,
  Divider,
  Paper,
  useTheme,
  alpha,
  Avatar
} from "@mui/material";
import { THEME_COLORS, GRADIENTS } from "../utils/constants";
import { 
  BarChart as StatisticsIcon,
  FindInPage as RecordsIcon,
  Add as SubmitIcon,
  ArrowForward as ArrowForwardIcon,
  Map as MapIcon
} from '@mui/icons-material';
import Hero from "../components/home/Hero";
// import MisconductExamplesVisual from "../components/common/MisconductExamplesVisual";

const dashboards = [
  {
    id: 1,
  title: "Request a Questionnaire",
    description: "Share information about misconduct you or someone you know has experienced. Help us build the most comprehensive database of official misconduct.",
    link: "/submit-case",
    color: THEME_COLORS.gold.primary,
    icon: <SubmitIcon fontSize="large" />
  },
  {
    id: 2,
    title: "Our Method",
    description: "Learn about our comprehensive approach to tracking misconduct patterns and identifying repeat offenders across the system.",
    link: "/method",
    color: THEME_COLORS.primary.dark,
    icon: <StatisticsIcon fontSize="large" />
  },
  {
    id: 3,
    title: "About Our Mission",
    description: "Discover how we're working to bring justice to those affected by official misconduct in the criminal legal system.",
    link: "/about",
    color: THEME_COLORS.primary.gray,
    icon: <RecordsIcon fontSize="large" />
  },
  {
    id: 4,
    title: "Judicial Agency Map",
    description: "Browse courts, prosecutors, law enforcement, and related agencies by state. A directory of institutions—not a map of records.",
    link: "/map",
    color: THEME_COLORS.gold.dark,
    icon: <MapIcon fontSize="large" />
  }
];

const Home = () => {
  const theme = useTheme();
  
  return (
    <Box>
      {/* Hero Section */}
      <Hero />
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        {/* Family Picture */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <img
            src={'/Family.png'}
            alt="Family"
            style={{
              maxWidth: '700px',
              width: '100%',
              height: 'auto',
              borderRadius: '24px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              objectFit: 'cover',
            }}
          />
        </Box>
        {/* Section Title */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '3px',
                  background: GRADIENTS.subtleGold,
                  borderRadius: '2px',
                }
            }}
          >
            How You Can Help
          </Typography>
          <Typography 
            variant="h6" 
            color="textSecondary"
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Join us in creating transparency and accountability in the criminal legal system.
          </Typography>
        </Box>
        
        {/* Dashboard Cards */}
        <Grid container spacing={4}>
          {dashboards.map((dashboard) => (
            <Grid item xs={12} md={4} key={dashboard.id} sx={{ display: 'flex' }}>
              <Card 
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden',
                  background: theme.palette.background.paper,
                  borderTop: `3px solid ${dashboard.id === 1 ? THEME_COLORS.gold.primary : 'transparent'}`,
                  borderLeft: `${dashboard.id === 2 ? `3px solid ${THEME_COLORS.gold.dark}` : 'none'}`,
                  borderRight: `${dashboard.id === 3 ? `3px solid ${THEME_COLORS.gold.darker}` : 'none'}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0px 12px 24px ${alpha(THEME_COLORS.neutral.black, 0.2)}`,
                  }
                }}
              >
                                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      gap: 2
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(THEME_COLORS.gold.primary, 0.15),
                        color: THEME_COLORS.gold.primary,
                        width: 56,
                        height: 56
                      }}
                    >
                      {dashboard.icon}
                    </Avatar>
                    <Typography 
                      variant="h5" 
                      component="h3" 
                      sx={{ 
                        fontWeight: 600,
                        background: GRADIENTS.textGold,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {dashboard.title}
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    color="textSecondary" 
                    sx={{ 
                      lineHeight: 1.6,
                      mb: 2
                    }}
                  >
                    {dashboard.description}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    component={Link} 
                    to={dashboard.link}
                    variant="text"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ 
                      color: THEME_COLORS.gold.primary,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: alpha(THEME_COLORS.gold.primary, 0.08),
                        transform: 'translateX(5px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Explore
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;